import { fetchWithChatFeedback } from '../api/chatFeedbackRequired';
/**
 * Business Impact auto-generation trigger helper.
 *
 * Per spec §2.6.3 / implplan §9, four user-action handlers in the app must
 * fire-and-forget a `POST /api/flows/{flowId}/business-impact/generate` call,
 * gated by the `shouldFireBusinessImpact` precondition truth-table:
 *
 *   1. ≥ 1 BI answer non-empty
 *   2. `summaryShort` not already set
 *   3. `editedAt` not set
 *
 * For triggers 3 & 4 (Regenerate retry path) the in-memory flow metadata can
 * be stale, so this helper always fetches fresh metadata from
 * `GET /api/flows/{flowId}` before deciding. For triggers 1 & 2 the freshness
 * is also helpful (covers the edge case where another tab populated
 * `summaryShort`) and costs only one cheap GET.
 *
 * The helper is fire-and-forget by design — all errors are swallowed so the
 * existing downstream call (Salient Characteristics / Round 2) is never
 * blocked. Callers should NOT await the returned promise.
 */

import { generateBusinessImpact } from '../api/businessImpactFunctions';
import { shouldFireBusinessImpact } from './businessImpact';
import type { FlowMetadata } from '../types/flow';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const userEmail =
    (typeof localStorage !== 'undefined' && localStorage.getItem('userEmail')) ||
    (typeof window !== 'undefined' && (window as any).userEmail) ||
    '';
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  return headers;
}

/**
 * Fetch fresh `FlowMetadata` from the backend so the BI gating decision is
 * never made against stale closure state. Returns `null` on any error.
 */
export async function fetchFlowMetadata(
  flowId: string
): Promise<FlowMetadata | null> {
  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}`, {
      method: 'GET',
      headers: buildHeaders(),
      credentials: 'include',
    });
    if (!response.ok) return null;
    const body = await response.json();
    return (body.metadata ?? body) as FlowMetadata;
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget BI auto-generation. Refreshes flow metadata, evaluates the
 * spec's precondition truth-table, and (only if all preconditions hold) POSTs
 * to the generate endpoint. All errors are swallowed.
 *
 * Returns the underlying promise mainly for tests — production callers should
 * NOT await it.
 */
export function tryFireBusinessImpact(
  flowId: string,
  hasNonEmptyBIAnswer: boolean,
  options: { reconcile?: boolean } = {},
): Promise<void> {
  console.info(
    `[BI trigger] called for flow=${flowId} hasNonEmpty=${hasNonEmptyBIAnswer}`
  );
  if (!hasNonEmptyBIAnswer && !options.reconcile) {
    console.info('[BI trigger] skip: no non-empty BI answer');
    return Promise.resolve();
  }
  console.info('[BI trigger] fetching flow metadata');
  return fetchFlowMetadata(flowId)
    .then((meta) => {
      if (!meta) {
        console.info('[BI trigger] skip: metadata fetch failed or null');
        return;
      }
      if (!shouldFireBusinessImpact(meta, hasNonEmptyBIAnswer)) {
        const summaryShort = meta.businessImpact?.summaryShort;
        const editedAt = meta.businessImpact?.editedAt;
        console.info(
          `[BI trigger] skip: preconditions not met (summaryShort=${JSON.stringify(summaryShort)}, editedAt=${JSON.stringify(editedAt)})`
        );
        return;
      }
      console.info(
        `[BI trigger] firing generateBusinessImpact for flow=${flowId}`
      );
      // Fire-and-forget; explicitly swallow per spec §2.6.3.
      generateBusinessImpact(flowId)
        .then(() => {
          console.info(
            `[BI trigger] generateBusinessImpact resolved for flow=${flowId}`
          );
        })
        .catch((err) => {
          console.info(
            `[BI trigger] generateBusinessImpact failed for flow=${flowId}: ${err}`
          );
          /* swallow */
        });
    })
    .catch(() => {
      /* swallow */
    });
}
